import React, { useState } from "react";
import { useTheme } from "@/context/ThemeContext";
import { companyInfo } from "@/data/mockData";
import { Phone, Mail, MapPin, Send, CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

export default function ContactPage() {
  const { isDark } = useTheme();
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = form.subject || `Inquiry from ${form.name}`;
    const body = `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\n\nMessage:\n${form.message}`;
    window.open(
      `mailto:${companyInfo.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    );
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    }, 3000);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const contactCards = [
    {
      icon: Phone,
      title: "Unit Manager",
      content: (
        <a
          href="tel:8341019995"
          className={`block hover:text-amber-400 transition-colors ${
            isDark ? "text-gray-400" : "text-gray-600"
          }`}
        >
          8341019995
        </a>
      ),
    },
    {
      icon: Phone,
      title: "Sales & Solutions",
      content: (
        <>
          <a
            href="tel:9000999995"
            className={`block hover:text-amber-400 transition-colors ${
              isDark ? "text-gray-400" : "text-gray-600"
            }`}
          >
            9000999995
          </a>
          <a
            href="tel:9246523153"
            className={`block hover:text-amber-400 transition-colors ${
              isDark ? "text-gray-400" : "text-gray-600"
            }`}
          >
            9246523153
          </a>
        </>
      ),
    },
    {
      icon: Mail,
      title: "Email",
      content: (
        <a
          href={`mailto:${companyInfo.email}`}
          className={`hover:text-amber-400 transition-colors break-all ${
            isDark ? "text-gray-400" : "text-gray-600"
          }`}
        >
          {companyInfo.email}
        </a>
      ),
    },
    {
      icon: MapPin,
      title: "Address",
      content: (
        <p className={isDark ? "text-gray-400" : "text-gray-600"}>
          Hyderabad, Telangana
        </p>
      ),
    },
  ];

  return (
    <div className={`pt-20 ${isDark ? "bg-slate-900" : "bg-white"}`}>
      {/* Header */}
      <section
        className={`py-24 ${
          isDark
            ? "bg-gradient-to-b from-slate-950 to-slate-900"
            : "bg-gradient-to-b from-gray-50 to-white"
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1
              className={`text-5xl md:text-6xl font-bold mb-6 ${
                isDark ? "text-white" : "text-slate-900"
              }`}
            >
              Get In{" "}
              <span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
                Touch
              </span>
            </h1>
            <p
              className={`text-xl leading-relaxed ${
                isDark ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Have questions about our products? Need a quote? We're here to
              help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Cards */}
            <div className="lg:col-span-1 space-y-6">
              {contactCards.map((card) => {
                const Icon = card.icon;
                return (
                  <div
                    key={card.title}
                    className={`group p-6 rounded-xl border transition-all duration-300 hover:scale-105 ${
                      isDark
                        ? "bg-gradient-to-br from-slate-950 to-slate-900 border-amber-600/20 hover:border-amber-500/50"
                        : "bg-white border-gray-200 hover:border-amber-400 shadow-sm hover:shadow-lg"
                    }`}
                  >
                    <div className="flex items-start space-x-4">
                      <div
                        className={`p-3 rounded-lg transition-colors ${
                          isDark
                            ? "bg-amber-500/10 group-hover:bg-amber-500/20"
                            : "bg-amber-50 group-hover:bg-amber-100"
                        }`}
                      >
                        <Icon className="w-6 h-6 text-amber-500" />
                      </div>
                      <div>
                        <h3
                          className={`font-semibold mb-2 ${
                            isDark ? "text-white" : "text-slate-900"
                          }`}
                        >
                          {card.title}
                        </h3>
                        {card.content}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div
                className={`p-8 rounded-xl border ${
                  isDark
                    ? "bg-gradient-to-br from-slate-950 to-slate-900 border-amber-600/20"
                    : "bg-white border-gray-200 shadow-sm"
                }`}
              >
                <h2
                  className={`text-3xl font-bold mb-6 ${
                    isDark ? "text-white" : "text-slate-900"
                  }`}
                >
                  Send Us a Message
                </h2>

                {submitted ? (
                  <div className="flex flex-col items-center py-12">
                    <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
                    <p
                      className={`text-lg font-semibold ${
                        isDark ? "text-white" : "text-slate-900"
                      }`}
                    >
                      Message Sent Successfully!
                    </p>
                    <p
                      className={`text-sm ${
                        isDark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      We'll get back to you shortly.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Name *
                        </label>
                        <Input
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          required
                          placeholder="Your name"
                          className={`${
                            isDark
                              ? "bg-slate-900 border-amber-600/20 focus:border-amber-500 text-white"
                              : ""
                          }`}
                        />
                      </div>
                      <div>
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Email *
                        </label>
                        <Input
                          name="email"
                          type="email"
                          value={form.email}
                          onChange={handleChange}
                          required
                          placeholder="your@email.com"
                          className={`${
                            isDark
                              ? "bg-slate-900 border-amber-600/20 focus:border-amber-500 text-white"
                              : ""
                          }`}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Phone
                        </label>
                        <Input
                          name="phone"
                          type="tel"
                          value={form.phone}
                          onChange={handleChange}
                          placeholder="Your phone number"
                          className={`${
                            isDark
                              ? "bg-slate-900 border-amber-600/20 focus:border-amber-500 text-white"
                              : ""
                          }`}
                        />
                      </div>
                      <div>
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Subject
                        </label>
                        <Input
                          name="subject"
                          value={form.subject}
                          onChange={handleChange}
                          placeholder="Inquiry subject"
                          className={`${
                            isDark
                              ? "bg-slate-900 border-amber-600/20 focus:border-amber-500 text-white"
                              : ""
                          }`}
                        />
                      </div>
                    </div>
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Message *
                      </label>
                      <Textarea
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        placeholder="Tell us about your requirements..."
                        className={`resize-none ${
                          isDark
                            ? "bg-slate-900 border-amber-600/20 focus:border-amber-500 text-white"
                            : ""
                        }`}
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full md:w-auto px-8 py-6 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 hover:scale-105"
                    >
                      Send Message
                      <Send className="ml-2 w-5 h-5" />
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className={`py-24 ${isDark ? "bg-slate-950" : "bg-gray-50"}`}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2
              className={`text-3xl font-bold mb-6 ${
                isDark ? "text-white" : "text-slate-900"
              }`}
            >
              Visit Our Location
            </h2>
            <p
              className={`mb-8 ${
                isDark ? "text-gray-400" : "text-gray-600"
              }`}
            >
              We're located in the heart of Hyderabad's industrial area,
              making it convenient for businesses across the region.
            </p>
            <div
              className={`p-4 rounded-xl border ${
                isDark ? "bg-slate-900 border-amber-600/20" : "bg-white border-gray-200"
              }`}
            >
              <iframe
                src={companyInfo.mapEmbedUrl}
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="rounded-lg"
                title="Mehra Mac Industries Location"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
